import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import {
  ManagedUserListResponseDto,
  ManagedUserSaveResponseDto,
  ManagedUserStatusResponseDto,
  ManagedUserTokenResponseDto,
} from './dtos/managed-user.dto';
import { ManagedUserMapper } from './mappers/managed-user.mapper';
import { ManagedUserPayloadMapper } from './mappers/managed-user-payload.mapper';
import {
  ManagedUsersPage,
  ManagedUserSaveResult,
  ManagedUserStatus,
} from '../models/managed-user.model';
import { ManagedUserDraft } from '../models/managed-user-draft.model';

@Injectable({ providedIn: 'root' })
export class UserApi {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentConfig = inject(ENV_CONFIG);
  private readonly _baseUrl = this._environmentConfig.pureSpectrum.url;
  private readonly _mapper = new ManagedUserMapper();
  private readonly _payloadMapper = new ManagedUserPayloadMapper();

  public users(
    page: number,
    limit: number,
    search: string,
  ): Observable<ManagedUsersPage> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('company', 'true');

    if (search) {
      params = params.set('search', search);
    }

    return this._httpClient
      .get<ManagedUserListResponseDto>(`${this._baseUrl}/users`, {
        params,
        context: withLoading('content'),
      })
      .pipe(
        map((response) => ({
          users: this._mapper.fromDtoList(response.companies),
          count: response.count,
        })),
      );
  }

  public save(draft: ManagedUserDraft): Observable<ManagedUserSaveResult> {
    return this._httpClient
      .post<ManagedUserSaveResponseDto>(
        `${this._baseUrl}/user`,
        this._payloadMapper.toPayload(draft),
        { context: withLoading('page') },
      )
      .pipe(map((response) => this._saveResult(response)));
  }

  public update(
    userId: number,
    draft: ManagedUserDraft,
  ): Observable<ManagedUserSaveResult> {
    return this._httpClient
      .put<ManagedUserSaveResponseDto>(
        `${this._baseUrl}/user/${userId}`,
        this._payloadMapper.toPayload(draft),
        { context: withLoading('page') },
      )
      .pipe(map((response) => this._saveResult(response)));
  }

  public updateStatus(
    userId: number,
    name: string,
    status: ManagedUserStatus,
  ): Observable<string> {
    return this._httpClient
      .put<ManagedUserStatusResponseDto>(
        `${this._baseUrl}/updateuserstatus/${userId}`,
        { encId: '', id: userId, name, status },
        { context: withLoading('page') },
      )
      .pipe(map((response) => response.msg));
  }

  public token(userId: number, isApiUser: boolean): Observable<string> {
    return this._httpClient
      .get<ManagedUserTokenResponseDto>(
        `${this._baseUrl}/getUserToken/${userId}/${isApiUser}`,
        { context: withLoading('page') },
      )
      .pipe(map((response) => response.token));
  }

  private _saveResult(
    response: ManagedUserSaveResponseDto,
  ): ManagedUserSaveResult {
    return {
      id: response.User.id,
      message: response.msg,
      isMultiBuyerManager: response.User.isMBManager,
    };
  }
}
